/**
 * Collection infinite scroll with back-navigation restore.
 * - Upgrades standard .pagination to IntersectionObserver loading.
 * - Uses Shopify section rendering API (section_id + page).
 * - Restores pages 1..N on back navigation when URL contains ?page=N.
 */
(function () {
  if (window.__collectionInfiniteScrollInitialized) {
    return;
  }

  window.__collectionInfiniteScrollInitialized = true;

  const GRID_SELECTOR = '.product-list__grid, .product-grid';
  const PAGINATION_SELECTOR = '.pagination';
  const NEXT_LINK_SELECTOR = 'a[rel="next"], [data-pagination-link][rel="next"]';
  const PRODUCT_SELECTOR = '.product-card, [data-product-card], product-card';
  // What is carried over from the next page: the cards and the campaign frames between them, in page order
  const APPEND_SELECTOR = PRODUCT_SELECTOR + ', .grid-frame';
  const SCROLL_STORAGE_KEY = 'collectionScrollY';
  const LOADER_STYLE_ID = 'infinite-scroll-style';
  const OBSERVER_ROOT_MARGIN = '0px 0px 600px 0px';
  const COUNT_PLACEHOLDER_FROM = '__FROM__';
  const COUNT_PLACEHOLDER_TO = '__TO__';
  const COUNT_PLACEHOLDER_TOTAL = '__COUNT__';
  const RESERVE_CLASS = 'infinite-scroll-reserve';

  const sections = new Map();

  function debounce(fn, delay) {
    let timeoutId;

    return function debounced() {
      const args = arguments;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(null, args);
      }, delay);
    };
  }

  function getSectionRoot(node) {
    return node.closest('.shopify-section') || node.closest('section');
  }

  function getSectionId(root) {
    if (!root || !root.id) {
      return null;
    }

    if (root.id.indexOf('shopify-section-') === 0) {
      return root.id.replace('shopify-section-', '');
    }

    return root.id;
  }

  function parsePageFromHref(href) {
    if (!href) {
      return null;
    }

    try {
      const url = new URL(href, window.location.origin);
      const page = parseInt(url.searchParams.get('page') || '', 10);

      return Number.isFinite(page) && page > 0 ? page : null;
    } catch (error) {
      return null;
    }
  }

  function getRequestedPage() {
    const page = parseInt(new URL(window.location.href).searchParams.get('page') || '', 10);

    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function ensureLoaderStyles() {
    if (document.getElementById(LOADER_STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = LOADER_STYLE_ID;
    style.textContent = [
      '.infinite-scroll-loader{display:none;align-items:center;justify-content:center;min-height:2.5rem;margin:1rem 0;}',
      '.infinite-scroll-loader.is-visible{display:flex;}',
      '.infinite-scroll-loader__spinner{width:18px;height:18px;border:2px solid rgba(0,0,0,.16);border-top-color:rgba(0,0,0,.42);border-radius:999px;animation:infinite-scroll-spin .7s linear infinite;}',
      '@keyframes infinite-scroll-spin{to{transform:rotate(360deg);}}',
      '.infinite-scroll-sentinel{display:block;height:1px;width:100%;}',
      // room kept for the next batch of cards, so nothing below the grid moves when they arrive
      '.' + RESERVE_CLASS + '{display:block;width:100%;pointer-events:none;background-color:var(--color-bg-white,#fff);}'
    ].join('');

    document.head.appendChild(style);
  }

  class InfiniteCollectionSection {
    constructor(root, grid, pagination, requestedPage) {
      this.root = root;
      this.sectionId = getSectionId(root);
      this.grid = grid;
      this.pagination = pagination;
      this.requestedPage = requestedPage;
      this.currentPage = 1;
      this.nextPage = this.extractNextPage(this.pagination);
      this.isLoading = false;
      this.isDone = false;
      this.observer = null;
      this.loader = null;
      this.sentinel = null;
      this.countElement = this.root.querySelector('[data-products-range-template]');
      this.totalElement = this.root.querySelector('[data-products-range-total]');
      this.pageSize = grid.querySelectorAll(PRODUCT_SELECTOR).length;
    }

    setupUi() {
      this.root.querySelectorAll('.infinite-scroll-loader, .infinite-scroll-sentinel').forEach(function (node) {
        node.remove();
      });

      if (this.pagination) {
        this.pagination.style.display = 'none';
      }

      this.loader = document.createElement('div');
      this.loader.className = 'infinite-scroll-loader';
      this.loader.innerHTML = '<span class="infinite-scroll-loader__spinner" aria-hidden="true"></span>';

      this.sentinel = document.createElement('div');
      this.sentinel.className = 'infinite-scroll-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');

      const host = this.pagination && this.pagination.parentNode ? this.pagination.parentNode : this.grid.parentNode;

      if (host) {
        host.insertBefore(this.loader, this.pagination || null);
        host.insertBefore(this.sentinel, this.pagination || null);
      }
    }

    /**
     * Keeps room for the next batch: empty blocks as high as a card, one per product still to come
     * (a page at most). The real cards take their place when they arrive, so the content under the grid
     * (text, footer) stays where it is. Loading starts when the first of these blocks comes near the screen.
     */
    clearReserve() {
      this.grid.querySelectorAll('.' + RESERVE_CLASS).forEach(function (node) {
        node.remove();
      });
    }

    reserveNextPage() {
      this.clearReserve();

      if (this.isDone || !this.nextPage || !this.totalElement || !this.pageSize) {
        return;
      }

      const cards = this.grid.querySelectorAll(PRODUCT_SELECTOR);
      const total = parseInt(this.totalElement.getAttribute('data-products-range-total') || '', 10);
      const lastCard = cards[cards.length - 1];
      const cardHeight = lastCard ? Math.round(lastCard.getBoundingClientRect().height) : 0;

      if (!Number.isFinite(total) || !cardHeight) {
        return;
      }

      const count = Math.min(this.pageSize, total - cards.length);
      if (count <= 0) {
        return;
      }

      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index += 1) {
        const block = document.createElement('div');
        block.className = RESERVE_CLASS;
        block.setAttribute('aria-hidden', 'true');
        block.style.height = cardHeight + 'px';
        fragment.appendChild(block);
      }

      this.grid.appendChild(fragment);
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;

      if (this.loader) {
        this.loader.classList.toggle('is-visible', isLoading);
      }
    }

    extractNextPage(paginationElement) {
      if (!paginationElement) {
        return null;
      }

      const nextLink = paginationElement.querySelector(NEXT_LINK_SELECTOR);
      if (!nextLink) {
        return null;
      }

      return parsePageFromHref(nextLink.getAttribute('href'));
    }

    getFetchUrl(page) {
      const url = new URL(window.location.href);

      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', this.sectionId);

      return url.pathname + url.search;
    }

    async fetchPagePayload(page) {
      const response = await fetch(this.getFetchUrl(page), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch page ' + page + ': ' + response.status);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const scopedRoot = doc.getElementById('shopify-section-' + this.sectionId) || doc.getElementById(this.sectionId) || doc;
      const nextGrid = scopedRoot.querySelector(GRID_SELECTOR);
      const nextPagination = scopedRoot.querySelector(PAGINATION_SELECTOR);

      return { nextGrid, nextPagination };
    }

    updatePagination(nextPagination) {
      if (!this.pagination) {
        this.pagination = nextPagination;
      }

      if (!nextPagination) {
        this.nextPage = null;
        this.isDone = true;

        if (this.pagination && this.pagination.parentNode) {
          this.pagination.parentNode.removeChild(this.pagination);
          this.pagination = null;
        }

        return;
      }

      if (this.pagination && this.pagination !== nextPagination) {
        this.pagination.innerHTML = nextPagination.innerHTML;
        this.pagination.style.display = 'none';
      }

      this.nextPage = this.extractNextPage(this.pagination || nextPagination);

      if (!this.nextPage) {
        this.isDone = true;
      }
    }

    appendProducts(sourceGrid) {
      if (!sourceGrid) {
        return 0;
      }

      const products = Array.from(sourceGrid.querySelectorAll(PRODUCT_SELECTOR));

      if (products.length > 0) {
        const fragment = document.createDocumentFragment();

        Array.from(sourceGrid.querySelectorAll(APPEND_SELECTOR)).forEach(function (node) {
          fragment.appendChild(node.cloneNode(true));
        });

        this.grid.appendChild(fragment);
        return products.length;
      }

      // Fallback for themes that do not use explicit product card selectors.
      if (sourceGrid.children.length > 0) {
        const fallbackFragment = document.createDocumentFragment();

        Array.from(sourceGrid.children).forEach(function (child) {
          fallbackFragment.appendChild(child.cloneNode(true));
        });

        this.grid.appendChild(fallbackFragment);
        return sourceGrid.children.length;
      }

      return 0;
    }

    updateProductsRangeCount(appendedProductsCount, mode) {
      if (!this.countElement) {
        return;
      }

      if (mode === 'replace') {
        const from = parseInt(this.countElement.getAttribute('data-products-range-from') || '', 10);
        const total = parseInt(this.countElement.getAttribute('data-products-range-total') || '', 10);
        if (!Number.isFinite(from) || !Number.isFinite(total)) {
          return;
        }

        const nextTo = Math.min(total, Math.max(from - 1, appendedProductsCount));
        this.countElement.setAttribute('data-products-range-to', String(nextTo));
      } else {
        if (appendedProductsCount <= 0) {
          return;
        }

        const currentTo = parseInt(this.countElement.getAttribute('data-products-range-to') || '', 10);
        const total = parseInt(this.countElement.getAttribute('data-products-range-total') || '', 10);

        if (!Number.isFinite(currentTo) || !Number.isFinite(total)) {
          return;
        }

        const nextTo = Math.min(total, currentTo + appendedProductsCount);
        this.countElement.setAttribute('data-products-range-to', String(nextTo));
      }

      const template = this.countElement.getAttribute('data-products-range-template');
      const from = parseInt(this.countElement.getAttribute('data-products-range-from') || '', 10);
      const to = parseInt(this.countElement.getAttribute('data-products-range-to') || '', 10);
      const total = parseInt(this.countElement.getAttribute('data-products-range-total') || '', 10);

      if (!template || !Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(total)) {
        return;
      }

      this.countElement.textContent = template
        .replace(COUNT_PLACEHOLDER_FROM, String(from))
        .replace(COUNT_PLACEHOLDER_TO, String(to))
        .replace(COUNT_PLACEHOLDER_TOTAL, String(total));
    }

    replaceProducts(sourceGrid) {
      if (!sourceGrid) {
        return 0;
      }

      this.grid.innerHTML = sourceGrid.innerHTML;
      return this.grid.querySelectorAll(PRODUCT_SELECTOR).length || this.grid.children.length;
    }

    updateHistory() {
      const url = new URL(window.location.href);

      url.searchParams.delete('section_id');
      url.searchParams.set('page', String(this.currentPage));

      history.replaceState(
        {
          page: this.currentPage,
          scrollY: window.scrollY
        },
        '',
        url.pathname + url.search
      );
    }

    async loadPage(page, mode, updateHistory) {
      if (this.isLoading || this.isDone || !this.sectionId) {
        return false;
      }

      this.setLoading(true);

      try {
        const payload = await this.fetchPagePayload(page);

        if (!payload.nextGrid) {
          this.isDone = true;
          this.disconnectObserver();
          return false;
        }

        // the cards take the room that was kept for them
        this.clearReserve();

        const loadedCount = mode === 'replace'
          ? this.replaceProducts(payload.nextGrid)
          : this.appendProducts(payload.nextGrid);

        if (loadedCount === 0) {
          this.isDone = true;
          this.disconnectObserver();
          return false;
        }

        this.currentPage = page;
        this.updateProductsRangeCount(loadedCount, mode);
        this.updatePagination(payload.nextPagination);

        if (updateHistory) {
          this.updateHistory();
        }

        if (this.isDone) {
          this.disconnectObserver();
        }

        return true;
      } catch (error) {
        return false;
      } finally {
        this.setLoading(false);
      }
    }

    async restoreToRequestedPage() {
      if (this.requestedPage <= 1) {
        this.currentPage = getRequestedPage();
        this.nextPage = this.extractNextPage(this.pagination);
        return;
      }

      const hasBasePage = await this.loadPage(1, 'replace', false);
      if (!hasBasePage) {
        this.isDone = true;
        return;
      }

      for (let page = 2; page <= this.requestedPage; page += 1) {
        const loaded = await this.loadPage(page, 'append', false);

        if (!loaded) {
          break;
        }
      }

      this.updateHistory();
    }

    async loadNextPageFromObserver() {
      if (!this.nextPage || this.isDone || this.isLoading) {
        return;
      }

      const loaded = await this.loadPage(this.nextPage, 'append', true);

      if (this.isDone) {
        // nothing more will come: give the kept room back
        this.clearReserve();
        return;
      }

      if (!loaded) {
        // the request failed (network): give the room back and try again when the end of the grid is reached
        this.clearReserve();
        this.disconnectObserver();
        window.setTimeout(() => {
          if (!this.isDone && this.root.contains(this.grid)) {
            this.connectObserver();
          }
        }, 3000);
        return;
      }

      this.reserveNextPage();
      this.connectObserver();
    }

    connectObserver() {
      if (!this.sentinel || this.isDone) {
        return;
      }

      this.disconnectObserver();

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadNextPageFromObserver();
          }
        });
      }, {
        root: null,
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: 0
      });

      // with room kept for the next batch the trigger is its first block, not the end of the grid
      this.observer.observe(this.grid.querySelector('.' + RESERVE_CLASS) || this.sentinel);
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    async bootstrap() {
      this.setupUi();
      await this.restoreToRequestedPage();

      if (!this.isDone) {
        this.reserveNextPage();
        this.connectObserver();
      }
    }
  }

  function collectEligibleSections() {
    const candidates = [];
    const paginations = document.querySelectorAll(PAGINATION_SELECTOR);

    paginations.forEach(function (pagination) {
      const root = getSectionRoot(pagination);
      if (!root) {
        return;
      }

      const existingSection = sections.get(root);
      if (existingSection) {
        const hasCurrentGrid = existingSection.grid && root.contains(existingSection.grid);
        const hasCurrentPagination = existingSection.pagination && root.contains(existingSection.pagination);

        if (hasCurrentGrid && hasCurrentPagination) {
          return;
        }

        existingSection.disconnectObserver();
        sections.delete(root);
      }

      const grid = root.querySelector(GRID_SELECTOR);
      if (!grid) {
        return;
      }

      const sectionId = getSectionId(root);
      if (!sectionId) {
        return;
      }

      candidates.push({ root, grid, pagination });
    });

    return candidates;
  }

  async function initSections(options) {
    const requestedPage = options && options.requestedPage ? options.requestedPage : getRequestedPage();
    const candidates = collectEligibleSections();

    for (const candidate of candidates) {
      const section = new InfiniteCollectionSection(
        candidate.root,
        candidate.grid,
        candidate.pagination,
        requestedPage
      );

      sections.set(candidate.root, section);
      await section.bootstrap();
    }
  }

  function restoreScrollPosition() {
    const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (!raw) {
      return;
    }

    const scrollY = parseInt(raw, 10);
    if (!Number.isFinite(scrollY) || scrollY < 0) {
      return;
    }

    requestAnimationFrame(function () {
      window.scrollTo(0, scrollY);
    });
  }

  function setupScrollPersistence() {
    const persistScroll = debounce(function () {
      sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
    }, 200);

    window.addEventListener('scroll', persistScroll, { passive: true });
    window.addEventListener('beforeunload', function () {
      sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
    });
  }

  function setupReinitHandlers() {
    const reinitialize = debounce(function () {
      initSections({ requestedPage: getRequestedPage() });
    }, 80);

    document.addEventListener('facet:update', reinitialize);

    const observer = new MutationObserver(function () {
      reinitialize();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  async function init() {
    ensureLoaderStyles();
    setupScrollPersistence();

    const requestedPage = getRequestedPage();
    await initSections({ requestedPage: requestedPage });

    if (requestedPage > 1) {
      restoreScrollPosition();
    }

    setupReinitHandlers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
